import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Asset, BootstrapData, CloudConnection, JobReference, MediaFile, MediaFileBundle, MediaRootDetail, MediaRootPermission, OneTimeAccessToken, ParametersMediaDefaultDeleteBehaviourEnum, WorkflowTransitionRequest } from 'elements-sdk'

import { APIService } from 'api/module'
import { BOOTSTRAP_DATA } from 'api/providers'
import { ClickService } from 'click/services/click.service'
import { BaseComponent, filterFalsy, replaceArrayItems } from 'common/helpers'
import { MessageBoxService } from 'common/util/module'
import { ParametersService } from 'main/services/parameters.service'
import { UploaderService } from 'main/services/uploader.service'
import { BaseOperation, MediaOperationContext } from 'media.operations/ops/base'
import { MediaOperationsService } from 'media.operations'
import { SharingService } from 'media.sharing/services/sharing.service'
import { WorkflowService } from 'media.workflow/services/workflow.service'
import { MediaConstants, MediaFileSort } from 'media/constants'
import { MediaAssetRatingsService } from 'media/services/mediaAssetRatings.service'
import { MediaClipboardService } from 'media/services/mediaClipboard.service'
import { MediaLibraryViewOptionsService, MediaLibraryViewStyle } from 'media/services/mediaLibraryViewOptions.service'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { MediaBrowserSelection } from 'media/services/mediaBrowserSelection.service'
import { PanelService } from 'panel/module'
import { TasksService } from 'tasks/services/tasks.service'
import { NotifyService } from 'common/util/services/notify.service'
import { coerceMediaFileBundle, coerceMediaFile } from '../migration'
import { MediaTagsManagerModalComponent } from './mediaTagsManagerModal.component'
import { MediaCreateFromTemplateSelectModalComponent } from './mediaCreateFromTemplateSelectModal.component'

@Component({
  selector: 'media-file-list-toolbar',
  templateUrl: './mediaFileListToolbar.component.pug',
  styleUrls: ['./mediaFileListToolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaFileListToolbarComponent extends BaseComponent {
  @Input() file?: MediaFile
  @Input() root?: MediaRootDetail
  @Input() dirs: MediaFile[]
  @Output() dirsChange = new EventEmitter<MediaFile[]>()
  @Input() bundles: MediaFileBundle[]
  @Output() bundlesChange = new EventEmitter<MediaFileBundle[]>()

  @Output() reloadRequest = new EventEmitter<void>()
  @Output() sortChange = new EventEmitter<MediaFileSort>()

  @Input() isFlat = false
  @Output() isFlatChange = new EventEmitter<boolean>()

  @Input() createDirectory: CallableFunction
  @Input() createShowroom?: CallableFunction
  @Input() createEditorProject: CallableFunction
  @Input() pasteItems: CallableFunction
  @Input() showFlatToggle = false
  @Input() isSharedLink?: OneTimeAccessToken
  @Input() isSearch = false

  permission?: MediaRootPermission
  myRating? = 0
  defaultDeleteBehaviour?: ParametersMediaDefaultDeleteBehaviourEnum
  defaultDeleteButtonLabel: string
  jobsDropdown: JobReference[]
  jobsButtons: JobReference[]
  ops: BaseOperation[]
  leftSideOps: BaseOperation[]
  rightSideOps: BaseOperation[]
  overflowMenuOps: BaseOperation[]
  selectionLeftSideOps: BaseOperation[]
  selectionRightSideOps: BaseOperation[]
  selectionOverflowMenuOps: BaseOperation[]
  opContext: MediaOperationContext
  allowedTransitions: any[] = []
  cloudConnections: CloudConnection[]
  viewStyle: MediaLibraryViewStyle
  sort: MediaFileSort
  showSorts = false

  constructor (
    public panel: PanelService,
    public mediaClipboard: MediaClipboardService,
    public uploader: UploaderService,
    public mediaBrowserSelection: MediaBrowserSelection,
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    private mediaConstants: MediaConstants,
    private translate: TranslateService,
    private mediaOperations: MediaOperationsService,
    private mediaPermissions: MediaPermissionsService,
    private parameters: ParametersService,
    private mediaAssetRatings: MediaAssetRatingsService,
    private click: ClickService,
    private api: APIService,
    private sharing: SharingService,
    private workflow: WorkflowService,
    private messageBox: MessageBoxService,
    private tasks: TasksService,
    private cdr: ChangeDetectorRef,
    private mediaLibraryViewOptions: MediaLibraryViewOptionsService,
    private ngbModal: NgbModal,
    private notify: NotifyService,
  ) {
    super()
    this.cloudConnections = bootstrapData.cloudConnections

    this.defaultDeleteButtonLabel = {
      disk: this.translate.instant('Delete original file'),
      database: this.translate.instant('Delete from database'),
      completely: this.translate.instant('Delete completely'),
    }[parameters.values.mediaDefaultDeleteBehaviour!]

    this.subs.add(this.mediaBrowserSelection.change$.subscribe(() => {
      cdr.markForCheck()
    }))
  }

  ngOnInit () {
    if (this.file) {
      this.permission = this.mediaPermissions.getPermissionForFile(this.file) ?? undefined
    }

    this.viewStyle = this.mediaLibraryViewOptions.getViewStyle(this.file, this.root)
    this.sort = this.mediaLibraryViewOptions.getSort(this.file)

    this.subs.add(this.mediaLibraryViewOptions.change$.subscribe(() => {
      this.viewStyle = this.mediaLibraryViewOptions.getViewStyle(this.file, this.root)
      this.cdr.markForCheck()
    }))

    if (this.root) {
      this.jobsDropdown = []
      this.jobsButtons = []
      for (const job of this.root.jobs?.filter(x => x.startable && !x.partOfWorkflowFor) ?? []) {
        if (job.showAsButton) {
          this.jobsButtons.push(job)
        } else {
          this.jobsDropdown.push(job)
        }
      }
    }

    this.ops = this.isSharedLink ? this.mediaOperations.ops.filter(x => x.showInSharedLinkView) : this.mediaOperations.ops

    this.leftSideOps = this.ops.filter(x => x.showInToolbar && x.weight <= 0)
    this.rightSideOps = this.ops.filter(x => x.showInToolbar && x.weight > 0)
    this.overflowMenuOps = this.ops.filter(x => x.showInOverflowMenu)

    this.selectionLeftSideOps = this.ops.filter(x => x.showInSelectionToolbar && x.weight <= 0)
    this.selectionRightSideOps = this.ops.filter(x => x.showInSelectionToolbar && x.weight > 0)
    this.selectionOverflowMenuOps = this.ops.filter(x => x.showInSelectionOverflowMenu)

    if (this.parameters.values.mediaDefaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Disk && this.permission?.allowDeleteFs) {
      this.defaultDeleteBehaviour = ParametersMediaDefaultDeleteBehaviourEnum.Disk
    }
    if (this.parameters.values.mediaDefaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Database && this.permission?.allowDeleteDb) {
      this.defaultDeleteBehaviour = ParametersMediaDefaultDeleteBehaviourEnum.Database
    }
    if (this.parameters.values.mediaDefaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Completely && this.permission?.allowDeleteFs && this.permission.allowDeleteDb) {
      this.defaultDeleteBehaviour = ParametersMediaDefaultDeleteBehaviourEnum.Completely
    }

    this.opContext = {
      root: this.root,
      location: this.file,
      permission: this.permission,
      isSearch: this.isSearch,
    }
  }

  isOpVisible (op: BaseOperation) {
    if (this.mediaBrowserSelection.hasSelection()) {
      return op.isVisibleFor({
        dirs: this.mediaBrowserSelection.dirs.map(coerceMediaFile),
        bundles: this.mediaBrowserSelection.bundles.map(coerceMediaFileBundle),
      }, this.opContext)
    } else if (this.file) {
      return op.isVisibleFor({
        dirs: [this.file],
        bundles: [],
      }, this.opContext)
    }
    return false
  }

  isOpEnabled (op: BaseOperation) {
    if (this.mediaBrowserSelection.hasSelection()) {
      return op.isEnabledFor({
        dirs: this.mediaBrowserSelection.dirs.map(coerceMediaFile),
        bundles: this.mediaBrowserSelection.bundles.map(coerceMediaFileBundle),
      }, this.opContext)
    } else if (this.file) {
      return op.isEnabledFor({
        dirs: [this.file],
        bundles: [],
      }, this.opContext)
    }
    return false
  }

  async runOp (op: BaseOperation) {
    if (this.mediaBrowserSelection.hasSelection()) {
      await op.run({
        dirs: this.mediaBrowserSelection.dirs.map(coerceMediaFile),
        bundles: this.mediaBrowserSelection.bundles.map(coerceMediaFileBundle),
      }, this.opContext)
      this.mediaBrowserSelection.clear()
    } else {
      if (!this.file) {
        throw new Error('Not in a directory')
      }
      await op.run({
        dirs: [this.file],
        bundles: [],
      }, this.opContext)
    }
    if (op.triggersReload) {
      this.reloadRequest.emit()
    }
  }

  onRatingSet () {
    if (this.myRating) {
      this.rateSelected(this.myRating)
      this.myRating = 0
      this.cdr.markForCheck()
      this.mediaBrowserSelection.clear()
    }
  }

  async rateSelected (rating: number) {
    const changedAssets: Promise<Asset>[] = []

    for (const bundle of this.mediaBrowserSelection.bundles) {
      const root = this.mediaPermissions.getPermissionForBundle(bundle)?.root ?? this.root?.id
      if (!root) {
        continue
      }
      if (bundle.asset) {
        changedAssets.push(this.mediaAssetRatings.rate(bundle.asset, root, rating))
      }
    }

    Promise.all(changedAssets).then(assets => {
      this.bundles = replaceArrayItems(
        this.bundles,
        b => assets.some(x => x.id === b.asset?.id),
        b => ({ ...b, asset: assets.find(x => x.id === b.asset?.id) }),
      )
      this.bundlesChange.emit(this.bundles)
    })
  }

  async onTagAdd (tag) {
    for (const dir of this.mediaBrowserSelection.dirs) {
      try {
        await this.messageBox.show({
          text: this.translate.instant('Recursively add tag "{tag}" on all assets inside "{folder}"?', { tag: tag.name, folder: dir.name }),
          positive: this.translate.instant('Add recursively'),
          negative: this.translate.instant('Cancel'),
        })
      } catch {
        return
      }
      await this.api.mediaLibrary.recursivelyTagMediaDirectory({
        id: dir.id,
        tagMediaDirectoryRequest: {
          tag: tag.id,
          add: true,
        },
      })
      this.notify.info(this.translate.instant('Recursive tagging started'))
    }
    for (const bundle of this.mediaBrowserSelection.bundles) {
      if (bundle.asset) {
        bundle.asset.tags = bundle.asset.tags.filter(x => x.id !== tag.id)
        bundle.asset.tags.push(tag)
        this.api.mediaLibrary.patchAsset({
          id: bundle.asset.id,
          root: this.root?.id,
          assetPartialUpdate: {
            tags: bundle.asset.tags,
          },
        })
      }
    }
    if (this.mediaBrowserSelection.bundles.length) {
      this.notify.success(this.translate.instant('Tag added'))
    }
  }

  async onTagRemove (tag) {
    for (const dir of this.mediaBrowserSelection.dirs) {
      try {
        await this.messageBox.show({
          text: this.translate.instant('Recursively remove tag "{tag}" on all assets inside "{folder}"?', { tag: tag.name, folder: dir.name }),
          positive: this.translate.instant('Remove recursively'),
          negative: this.translate.instant('Cancel'),
        })
      } catch {
        return
      }
      await this.api.mediaLibrary.recursivelyTagMediaDirectory({
        id: dir.id,
        tagMediaDirectoryRequest: {
          tag: tag.id,
          add: false,
        },
      })
      this.notify.info(this.translate.instant('Recursive tagging started'))
    }
    for (const bundle of this.mediaBrowserSelection.bundles) {
      if (!bundle.asset) {
        continue
      }
      bundle.asset.tags = bundle.asset.tags.filter(x => x !== tag.id)
      this.api.mediaLibrary.patchAsset({
        id: bundle.asset.id,
        root: this.root?.id,
        assetPartialUpdate: {
          tags: bundle.asset.tags,
        },
      })
    }
    if (this.mediaBrowserSelection.bundles.length) {
      this.notify.success(this.translate.instant('Tag removed'))
    }
  }

  deleteSelectedWithDefaultBehaviour () {
    if (this.defaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Disk) {
      this.deleteSelected(true, false)
    }
    if (this.defaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Database) {
      this.deleteSelected(false, true)
    }
    if (this.defaultDeleteBehaviour === ParametersMediaDefaultDeleteBehaviourEnum.Completely) {
      this.deleteSelected(true, true)
    }
  }

  deleteSelected (deleteFromStorage: boolean, deleteFromDatabase: boolean) {
    if (!deleteFromDatabase && !deleteFromStorage) {
      this.doDeleteSelected(deleteFromStorage, deleteFromDatabase)
      return
    }

    let msg = ''
    if (deleteFromStorage) {
      msg += this.translate.instant('Files will be deleted from the disk permanently.') + ' '
    }
    if (deleteFromDatabase) {
      msg += this.translate.instant('Asset information including comments, ratings and custom fields will be deleted from the database permanently.') + ' '
    }

    this.messageBox.show({ text: msg, positive: this.translate.instant('Delete'), negative: this.translate.instant('Cancel'), danger: true })
      .then(() => this.doDeleteSelected(deleteFromStorage, deleteFromDatabase), () => null)
  }

  async doDeleteSelected (deleteFromStorage: boolean, deleteFromDatabase: boolean) {
    const dirsForDeletion: MediaFile[] = []
    const bundlesForDeletion: MediaFileBundle[] = []
    const assetsForDeletion: Asset[] = []

    for (const dir of this.mediaBrowserSelection.dirs) {
      dirsForDeletion.push(dir)
    }

    for (const bundle of this.mediaBrowserSelection.bundles) {
      if (bundle.asset && bundle.asset.bundles.length === 1) {
        assetsForDeletion.push(bundle.asset)
      }

      if (bundle.id > 0) {
        bundlesForDeletion.push(bundle)
      }
    }

    if (deleteFromStorage) {
      this.dirs = replaceArrayItems(
        this.dirs,
        f => dirsForDeletion.includes(f),
        f => ({ ...f, present: false }),
      )
      this.dirsChange.emit(this.dirs)

      this.bundles = replaceArrayItems(
        this.bundles,
        b => dirsForDeletion.includes(b),
        b => ({ ...b, mainfile: { ...b.mainfile, present: false } }),
      )
      this.bundlesChange.emit(this.bundles)
    }

    try {
      await this.api.mediaLibrary.deleteMediaLibraryObjects({
        mediaLibraryDeleteRequest: {
          bundles: bundlesForDeletion.map(x => x.id),
          files: dirsForDeletion.map(x => x.id),
          assets: assetsForDeletion.map(x => x.id),
          deleteFromStorage,
          deleteFromDatabase,
        },
      })

      if (deleteFromDatabase) {
        this.dirs = this.dirs.filter(x => !dirsForDeletion.includes(x))
        this.bundles = this.bundles.filter(x => !bundlesForDeletion.includes(x))
        this.dirsChange.emit(this.dirs)
        this.bundlesChange.emit(this.bundles)
      }

      this.notify.success(this.translate.instant('Deleted'))
    } catch (e) {
      this.notify.error(this.translate.instant('Deletion failed'), e)
    }

    this.mediaBrowserSelection.clear()
  }

  shareSelectedToCloud (connection) {
    const assets = filterFalsy(this.mediaBrowserSelection.bundles.filter(x => x.asset && this.click.supportedAssetTypes.includes(x.asset.type ?? '') && x.asset.proxiesGenerated).map(x => x.asset))
    if (assets.length === 0) {
      this.notify.error(this.translate.instant('No files selected that are suitable for upload'))
    } else {
      this.click.share(connection, assets).catch(() => null)
    }
    this.mediaBrowserSelection.clear()
  }

  async sendSelected () {
    this.sharing.sendToHomeWorkspace({
      paths: this.mediaBrowserSelection.dirs.map(x => x.fullPath),
      bundles: this.mediaBrowserSelection.bundles.filter(b => b.mainfile.present),
    })
    this.mediaBrowserSelection.clear()
  }

  async addToShowroom () {
    const bundles = this.mediaBrowserSelection.bundles.filter(bundle => bundle.asset && bundle.mainfile.present)

    if (bundles.length > 0) {
      await this.sharing.addToShowroom(bundles)
      this.mediaBrowserSelection.clear()
    }
  }

  checkTransitions () {
    if (!this.root?.workflow) {
      throw new Error('Not in a MR')
    }

    // if a dir is selected, allow all
    const states = this.mediaBrowserSelection.bundles.map(bundle => bundle.asset?.workflowState)
    this.allowedTransitions = this.root.workflow.transitions as any
    if (!this.mediaBrowserSelection.dirs.length) {
      this.allowedTransitions = this.allowedTransitions.filter(t => states.includes(t.from))
    }
    this.allowedTransitions = this.allowedTransitions.filter(t => this.root!.workflowJobs.some(j => j.id === t.job_id))
  }

  async transitionWorkflow (transition) {
    if (!this.root) {
      throw new Error('Not in a MR')
    }
    const options: WorkflowTransitionRequest = {
      directories: [],
      bundles: [],
      root: this.root.id,
      job: transition.job_id,
    }
    await Promise.all(this.mediaBrowserSelection.dirs.map(dir =>
      this.messageBox.show({
        text: this.translate.instant('Recursively run workflow action "{action}" on all assets inside "{folder}"?', { action: transition.name, folder: dir.name }),
        positive: this.translate.instant('Run recursively'),
        negative: this.translate.instant('Cancel'),
      }).then(() => {
        options.directories!.push(dir.id)
      }).catch(() => null),
    ))
    for (const bundle of this.mediaBrowserSelection.bundles) {
      if (bundle.asset && bundle.asset.workflowState === transition.from) {
        try {
          this.workflow.ensureTransitionAllowed(transition, bundle, this.file)
        } catch (error) {
          this.notify.error(this.translate.instant('Transition failed for %s').replace('%s', bundle.name), error.message)
          return
        }

        bundle.asset.workflowState = transition.to
        options.bundles!.push(bundle.id)
      }
    }
    if (!options.directories!.length && !options.bundles!.length) {
      return
    }

    this.workflow.transition(options)
    this.mediaBrowserSelection.clear()
  }

  async startJobWithSelection (job) {
    const dirPaths = this.mediaBrowserSelection.dirs.map(x => x.fullPath)
    const bundlePaths = this.mediaBrowserSelection.bundles.map(x => x.mainfile.fullPath)
    const allPaths = dirPaths.concat(bundlePaths)

    if ((job.inputType === 'dir' || job.inputType === 'dirs') && this.mediaBrowserSelection.bundles.length) {
      this.notify.error(this.translate.instant('Selected task only accepts directories'))
    }

    if (job.inputType === 'dirs') {
      await this.tasks.startJob(job, { input: dirPaths })
    } else if (job.inputType === 'paths') {
      await this.tasks.startJob(job, { input: allPaths })
    } else if (job.inputType === 'dir') {
      await Promise.all(dirPaths.map(x => this.tasks.startJob(job, { input: x })))
    } else if (job.inputType === 'path') {
      await Promise.all(allPaths.map(x => this.tasks.startJob(job, { input: x })))
    } else {
      await this.tasks.startJob(job, {})
    }
    this.mediaBrowserSelection.clear()
  }

  async showCreateFromTemplate () {
    if (!this.file) {
      throw new Error('Not in a directory')
    }
    const m = this.ngbModal.open(MediaCreateFromTemplateSelectModalComponent)
    m.componentInstance.root = this.root
    const { template, name, customFields } = await m.result.catch(() => ({}))
    if (!template) {
      return
    }
    try {
      await this.api.mediaLibrary.instantiateMediaFileTemplate({
        id: template.id,
        instantiateFileTemplateRequest: {
          parent: this.file.id,
          name,
          customFields,
        },
      })
      this.notify.success(this.translate.instant('Created'))
      this.reloadRequest.emit()
    } catch (error) {
      this.notify.errorFromResponse(this.translate.instant('Could not create'), error)
    }
  }

  async showTagsManager () {
    const m = this.ngbModal.open(MediaTagsManagerModalComponent)
    m.componentInstance.root = this.root
    await m.result.catch(() => null)
    this.reloadRequest.emit()
  }

  async startJobWithDirectory (job) {
    if (!this.file) {
      throw new Error('Not in a directory')
    }
    const vars = {
      input: job.inputType === 'dir' || job.inputType === 'path' ? this.file.fullPath : [this.file.fullPath],
    }
    await this.tasks.startJob(job, vars)
  }

  async shareSelection () {
    let rootId = 0
    const permissions: MediaRootPermission[] = []
    for (const bundle of this.mediaBrowserSelection.bundles) {
      const permission = this.mediaPermissions.getPermissionForBundle(bundle)
      if (permission) {
        permissions.push(permission)
        rootId = permission.root
      }
    }
    for (const dir of this.mediaBrowserSelection.dirs) {
      const permission = this.mediaPermissions.getPermissionForFile(dir)
      if (permission) {
        permissions.push(permission)
        rootId = permission.root
      }
    }

    await this.sharing.show(
      this.mediaBrowserSelection.bundles,
      this.mediaBrowserSelection.dirs,
      this.permission ?? this.mediaPermissions.mergePermissions(permissions),
      this.root ?? await this.api.mediaLibrary.getMediaRoot({ id: rootId }),
    ).catch(() => null)
    this.mediaBrowserSelection.clear()
    this.reloadRequest.emit()
  }

  invertSelection () {
    for (const x of this.dirs) {
      this.mediaBrowserSelection.toggleSelected(x)
    }
    for (const x of this.bundles) {
      this.mediaBrowserSelection.toggleSelected(x)
    }
  }

  saveViewStyle () {
    this.mediaLibraryViewOptions.setViewStyle(this.file, this.root, this.viewStyle)
  }

  setSort (sort: MediaFileSort) {
    if (this.sort !== sort) {
      this.sort = sort
      this.sortChange.emit(sort)
      if (this.file) {
        this.mediaLibraryViewOptions.setSort(this.file, sort)
      }
      this.mediaLibraryViewOptions.defaultSort = sort
    }
  }

  isSelectionPresentOnDisk () {
    return (!this.file || this.file.present) && this.mediaBrowserSelection.dirs.every(x => x.present) && this.mediaBrowserSelection.bundles.every(x => x.mainfile.present)
  }
}
